"use client";

import { c as _c } from "react/compiler-runtime";
import { useChains } from "@opensea/app-kit";
import { useNumberFormatter } from "@opensea/i18n";
import { Flex, FlexCenter, ItemAvatar, ItemContent, ItemTitle, Media, NumberDisplay, QuantityBadge, QuantityInput, SkeletonBlock, SkeletonLine, SpaceBetween, TABLE_SIZES, TableCell, TableRow, TextBody, TextOverflowTooltip, UnstyledButton, useTable } from "@opensea/ui-kit";
import { Close } from "@opensea/ui-kit/icons";
import { bn } from "@opensea/utils";
import { readFragment } from "gql.tada";
import { Controller, useFieldArray, useFormContext, useFormState, useWatch } from "react-hook-form";
import { useTranslations } from "use-intl";
import { InlineSwapButton } from "../../../../../components/InlineSwapButton";
import { Item } from "../../../../../components/Item";
import { OfferAmountInput } from "../../../../../components/OfferAmountInput";
import { RarityBadge } from "../../../../../components/RarityBadge";
import { useSyncFormFields } from "../../../../../hooks/useSyncFormField";
import { calculateOrderTotals } from "../../../../../lib/fees";
import { useBalances } from "../../../../../providers/BalancesProvider";
import { ITEM_OFFER_HIGH_SEVERITY_WARNING_THRESHOLD, OFFER_MAX_DECIMALS, ORDER_PRICE_LOW_SEVERITY_WARNING_THRESHOLD_PERCENTAGE } from "../../../constants";
import { CreateOfferFlowTableRowFragment } from "./CreateOfferFlowTableRow.fragment";
import { CREATE_OFFER_FLOW_TABLE_CLASSNAMES } from "./constants";
export const CreateOfferFlowTableRow = t0 => {
  const $ = _c(122);
  const {
    item: itemKey,
    context,
    index
  } = t0;
  const t = useTranslations("wallet.CreateOfferFlowTableRow");
  const item = readFragment(CreateOfferFlowTableRowFragment, itemKey);
  const {
    getNativeCurrency,
    getOfferCurrency
  } = useChains();
  const {
    control,
    setValue
  } = useFormContext();
  let t1;
  if ($[0] !== control) {
    t1 = {
      control
    };
    $[0] = control;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  const {
    errors
  } = useFormState(t1);
  let t2;
  if ($[2] !== control) {
    t2 = {
      keyName: "_id",
      control,
      name: "offers"
    };
    $[2] = control;
    $[3] = t2;
  } else {
    t2 = $[3];
  }
  const {
    remove
  } = useFieldArray(t2);
  let t3;
  if ($[4] !== control) {
    t3 = {
      control,
      name: "offers"
    };
    $[4] = control;
    $[5] = t3;
  } else {
    t3 = $[5];
  }
  const offers = useWatch(t3);
  const offer = offers[index];
  const format = useNumberFormatter();
  const isReview = context?.isReview ?? false;
  const error = errors.offers?.[index]?.price;
  const quantityError = errors.offers?.[index]?.quantity;
  let t4;
  if ($[6] !== offer) {
    t4 = calculateOrderTotals(offer ? [offer] : []);
    $[6] = offer;
    $[7] = t4;
  } else {
    t4 = $[7];
  }
  const {
    withoutFees
  } = t4;
  const {
    getBalanceOfCurrencyBySymbol
  } = useBalances();
  const {
    size
  } = useTable();
  const imageSize = TABLE_SIZES[size].image;
  const isFungible = item?.isFungible;
  const bestOffer = item?.bestOffer?.pricePerItem.token;
  const bestListing = item?.bestListing?.pricePerItem.token;
  const priceWarning = bestOffer && offer?.price && offer.price > 0 && bn(offer.price).gt(bn(bestOffer.unit).times(1 + ORDER_PRICE_LOW_SEVERITY_WARNING_THRESHOLD_PERCENTAGE).decimalPlaces(OFFER_MAX_DECIMALS)) ? t("priceAboveBestOffer", {
    price: bn(bestOffer.unit).decimalPlaces(OFFER_MAX_DECIMALS).toString(),
    symbol: bestOffer.symbol
  }) : undefined;
  const showHighSevPriceListingWarning = bestListing && Boolean(priceWarning) && offer?.price && bn(offer.price).gt(bn(bestListing.unit));
  const showHighSevPriceOfferWarning = Boolean(priceWarning) && bestOffer && bn(offer.price).gt(bn(bestOffer.unit).times(ITEM_OFFER_HIGH_SEVERITY_WARNING_THRESHOLD));
  const errorMessage = showHighSevPriceListingWarning ? t("priceAboveBestListing", {
    price: bn(bestListing.unit).toString(),
    symbol: bestListing.symbol
  }) : priceWarning;
  const t5 = `offers.${index}.shouldConfirmPrice`;
  const t6 = Boolean(showHighSevPriceListingWarning || showHighSevPriceOfferWarning);
  let t7;
  if ($[8] !== t5 || $[9] !== t6) {
    t7 = [{
      name: t5,
      value: t6
    }];
    $[8] = t5;
    $[9] = t6;
    $[10] = t7;
  } else {
    t7 = $[10];
  }
  let t8;
  if ($[11] !== setValue || $[12] !== t7) {
    t8 = {
      fields: t7,
      setValue
    };
    $[11] = setValue;
    $[12] = t7;
    $[13] = t8;
  } else {
    t8 = $[13];
  }
  useSyncFormFields(t8);
  let t9;
  if ($[14] !== quantityError) {
    t9 = quantityError && <TextBody color="error-1" size="xs">{quantityError.message}</TextBody>;
    $[14] = quantityError;
    $[15] = t9;
  } else {
    t9 = $[15];
  }
  const quantityErrorContent = t9;
  let t10;
  if ($[16] !== control || $[17] !== index || $[18] !== isFungible || $[19] !== isReview || $[20] !== offer?.quantity || $[21] !== quantityError) {
    t10 = isFungible ? isReview ? <QuantityBadge className="ml-1">{offer?.quantity}</QuantityBadge> : <Controller control={control} name={`offers.${index}.quantity`} render={t11 => {
      const {
        field
      } = t11;
      return <QuantityInput className="ml-1" error={Boolean(quantityError)} min={0} onValueChange={field.onChange} size="sm" value={field.value} />;
    }} /> : null;
    $[16] = control;
    $[17] = index;
    $[18] = isFungible;
    $[19] = isReview;
    $[20] = offer?.quantity;
    $[21] = quantityError;
    $[22] = t10;
  } else {
    t10 = $[22];
  }
  const quantityContent = t10;
  if (!item) {
    let t11;
    if ($[23] === Symbol.for("react.memo_cache_sentinel")) {
      t11 = <CreateOfferFlowTableRowSkeleton />;
      $[23] = t11;
    } else {
      t11 = $[23];
    }
    return t11;
  }
  const offerSymbol = item.chain.offerCurrency.symbol;
  const balance = getBalanceOfCurrencyBySymbol({
    chainIdentifier: item.chain.identifier,
    symbol: offerSymbol
  });
  const t11 = offer?.tokenBalance ?? 0;
  let t12;
  if ($[24] !== offer?.price || $[25] !== offer?.quantity || $[26] !== t11) {
    const tokenBalance = bn(t11);
    const totalOfferAmount = (offer?.price ?? 0) * (offer?.quantity || 1);
    t12 = tokenBalance?.lt(totalOfferAmount);
    $[24] = offer?.price;
    $[25] = offer?.quantity;
    $[26] = t11;
    $[27] = t12;
  } else {
    t12 = $[27];
  }
  const hasInsufficientBalance = t12;
  const nativeCurrency = getNativeCurrency(item.chain.identifier);
  const offerCurrency = getOfferCurrency(item.chain.identifier);
  const priceInfo = priceWarning && !isReview || error ? <TextBody color={priceWarning && !showHighSevPriceListingWarning && !showHighSevPriceOfferWarning && !error ? "warning" : "error-1"} size="xs">{error ? error.message : errorMessage}</TextBody> : isReview ? null : <TextBody color="text-secondary" size="xs">{t("balance")}{" "}{format(balance?.quantity ?? 0, {
      display: "standard",
      suffix: offerSymbol
    })}</TextBody>;
  let t13;
  if ($[28] !== imageSize || $[29] !== item.imageUrl) {
    t13 = <ItemAvatar className="rounded" size={imageSize} src={item.imageUrl} />;
    $[28] = imageSize;
    $[29] = item.imageUrl;
    $[30] = t13;
  } else {
    t13 = $[30];
  }
  const t14 = item.name || `#${item.tokenId}`;
  let t15;
  if ($[31] !== t14) {
    t15 = <TextOverflowTooltip><ItemTitle>{t14}</ItemTitle></TextOverflowTooltip>;
    $[31] = t14;
    $[32] = t15;
  } else {
    t15 = $[32];
  }
  let t16;
  if ($[33] !== item) {
    t16 = <RarityBadge item={item} />;
    $[33] = item;
    $[34] = t16;
  } else {
    t16 = $[34];
  }
  let t17;
  if ($[35] !== quantityContent) {
    t17 = <Media greaterThanOrEqual="md">{quantityContent}</Media>;
    $[35] = quantityContent;
    $[36] = t17;
  } else {
    t17 = $[36];
  }
  let t18;
  if ($[37] !== t15 || $[38] !== t16 || $[39] !== t17) {
    t18 = <ItemContent><FlexCenter className="gap-2">{t15}{t16}{t17}</FlexCenter></ItemContent>;
    $[37] = t15;
    $[38] = t16;
    $[39] = t17;
    $[40] = t18;
  } else {
    t18 = $[40];
  }
  let t19;
  if ($[41] !== t13 || $[42] !== t18) {
    t19 = <Item variant="unstyled">{t13}{t18}</Item>;
    $[41] = t13;
    $[42] = t18;
    $[43] = t19;
  } else {
    t19 = $[43];
  }
  const itemLockup = t19;
  let t20;
  if ($[44] !== hasInsufficientBalance || $[45] !== index || $[46] !== isReview || $[47] !== item.chain || $[48] !== nativeCurrency || $[49] !== offer?.price || $[50] !== offerCurrency || $[51] !== offerSymbol || $[52] !== priceWarning) {
    t20 = isReview ? <TextOverflowTooltip><TextBody><NumberDisplay suffix={offerSymbol} value={offer?.price} /></TextBody></TextOverflowTooltip> : <Flex className="items-center gap-2"><OfferAmountInput autoFocus={index === 0} chain={item.chain.identifier} offerIndex={index} warning={Boolean(priceWarning)} />{hasInsufficientBalance && <InlineSwapButton initialFromCurrency={{
        symbol: nativeCurrency.symbol,
        contractAddress: nativeCurrency.contractAddress,
        chainIdentifier: item.chain.identifier
      }} initialToCurrency={{
        symbol: offerCurrency.symbol,
        contractAddress: offerCurrency.contractAddress,
        chainIdentifier: item.chain.identifier
      }} />}</Flex>;
    $[44] = hasInsufficientBalance;
    $[45] = index;
    $[46] = isReview;
    $[47] = item.chain;
    $[48] = nativeCurrency;
    $[49] = offer?.price;
    $[50] = offerCurrency;
    $[51] = offerSymbol;
    $[52] = priceWarning;
    $[53] = t20;
  } else {
    t20 = $[53];
  }
  const priceContent = t20;
  const showDeleteCell = offers.length > 1 && !isReview;
  let t21;
  if ($[54] !== index || $[55] !== remove) {
    t21 = () => {
      remove(index);
    };
    $[54] = index;
    $[55] = remove;
    $[56] = t21;
  } else {
    t21 = $[56];
  }
  let t22;
  if ($[57] === Symbol.for("react.memo_cache_sentinel")) {
    t22 = <Close fill="interactive-primary-styles" size={20} />;
    $[57] = t22;
  } else {
    t22 = $[57];
  }
  let t23;
  if ($[58] !== t21) {
    t23 = <UnstyledButton onClick={t21}>{t22}</UnstyledButton>;
    $[58] = t21;
    $[59] = t23;
  } else {
    t23 = $[59];
  }
  const deleteButton = t23;
  let t24;
  if ($[60] !== itemLockup) {
    t24 = <TableCell className={CREATE_OFFER_FLOW_TABLE_CLASSNAMES.item}>{itemLockup}</TableCell>;
    $[60] = itemLockup;
    $[61] = t24;
  } else {
    t24 = $[61];
  }
  const t25 = isFungible ? quantityContent : priceContent;
  let t26;
  if ($[62] !== t25) {
    t26 = <TableCell className={CREATE_OFFER_FLOW_TABLE_CLASSNAMES.price}>{t25}</TableCell>;
    $[62] = t25;
    $[63] = t26;
  } else {
    t26 = $[63];
  }
  let t27;
  if ($[64] !== deleteButton || $[65] !== showDeleteCell) {
    t27 = showDeleteCell && <TableCell className={CREATE_OFFER_FLOW_TABLE_CLASSNAMES.remove}>{deleteButton}</TableCell>;
    $[64] = deleteButton;
    $[65] = showDeleteCell;
    $[66] = t27;
  } else {
    t27 = $[66];
  }
  let t28;
  if ($[67] !== t24 || $[68] !== t26 || $[69] !== t27) {
    t28 = <TableRow>{t24}{t26}{t27}</TableRow>;
    $[67] = t24;
    $[68] = t26;
    $[69] = t27;
    $[70] = t28;
  } else {
    t28 = $[70];
  }
  let t29;
  if ($[71] !== isFungible || $[72] !== priceContent || $[73] !== showDeleteCell || $[74] !== t) {
    t29 = isFungible && <TableRow><TableCell className={CREATE_OFFER_FLOW_TABLE_CLASSNAMES.item}><TextBody weight="semibold">{t("offerPrice")}</TextBody></TableCell><TableCell className={CREATE_OFFER_FLOW_TABLE_CLASSNAMES.price}>{priceContent}</TableCell>{showDeleteCell && <TableCell className={CREATE_OFFER_FLOW_TABLE_CLASSNAMES.remove} />}</TableRow>;
    $[71] = isFungible;
    $[72] = priceContent;
    $[73] = showDeleteCell;
    $[74] = t;
    $[75] = t29;
  } else {
    t29 = $[75];
  }
  let t30;
  if ($[76] !== priceInfo) {
    t30 = <Flex className="justify-end">{priceInfo}</Flex>;
    $[76] = priceInfo;
    $[77] = t30;
  } else {
    t30 = $[77];
  }
  let t31;
  if ($[78] !== quantityErrorContent || $[79] !== t28 || $[80] !== t29 || $[81] !== t30) {
    t31 = <Media lessThan="md">{t28}{quantityErrorContent}{t29}{t30}</Media>;
    $[78] = quantityErrorContent;
    $[79] = t28;
    $[80] = t29;
    $[81] = t30;
    $[82] = t31;
  } else {
    t31 = $[82];
  }
  let t32;
  if ($[83] !== itemLockup) {
    t32 = <TableCell className={CREATE_OFFER_FLOW_TABLE_CLASSNAMES.item}>{itemLockup}</TableCell>;
    $[83] = itemLockup;
    $[84] = t32;
  } else {
    t32 = $[84];
  }
  const t33 = item.collection?.floorPrice?.pricePerItem.token.symbol;
  const t34 = item.collection?.floorPrice?.pricePerItem.token.unit;
  let t35;
  if ($[85] !== t33 || $[86] !== t34) {
    t35 = <TableCell className={CREATE_OFFER_FLOW_TABLE_CLASSNAMES.floor}><TextOverflowTooltip><TextBody><NumberDisplay suffix={t33} value={t34} /></TextBody></TextOverflowTooltip></TableCell>;
    $[85] = t33;
    $[86] = t34;
    $[87] = t35;
  } else {
    t35 = $[87];
  }
  const t36 = item.bestOffer?.pricePerItem.token.symbol;
  const t37 = item.bestOffer?.pricePerItem.token.unit;
  let t38;
  if ($[88] !== t36 || $[89] !== t37) {
    t38 = <TableCell className={CREATE_OFFER_FLOW_TABLE_CLASSNAMES.bestOffer}><TextOverflowTooltip><TextBody><NumberDisplay suffix={t36} value={t37} /></TextBody></TextOverflowTooltip></TableCell>;
    $[88] = t36;
    $[89] = t37;
    $[90] = t38;
  } else {
    t38 = $[90];
  }
  const t39 = item.lastSale?.token.symbol;
  const t40 = item.lastSale?.token.unit;
  let t41;
  if ($[91] !== t39 || $[92] !== t40) {
    t41 = <TableCell className={CREATE_OFFER_FLOW_TABLE_CLASSNAMES.cost}><TextOverflowTooltip><TextBody><NumberDisplay suffix={t39} value={t40} /></TextBody></TextOverflowTooltip></TableCell>;
    $[91] = t39;
    $[92] = t40;
    $[93] = t41;
  } else {
    t41 = $[93];
  }
  let t42;
  if ($[94] !== offerSymbol || $[95] !== withoutFees) {
    t42 = <TableCell className={CREATE_OFFER_FLOW_TABLE_CLASSNAMES.proceeds}><TextOverflowTooltip><TextBody><NumberDisplay suffix={offerSymbol} value={withoutFees} /></TextBody></TextOverflowTooltip></TableCell>;
    $[94] = offerSymbol;
    $[95] = withoutFees;
    $[96] = t42;
  } else {
    t42 = $[96];
  }
  let t43;
  if ($[97] !== priceContent) {
    t43 = <TableCell className={CREATE_OFFER_FLOW_TABLE_CLASSNAMES.price}>{priceContent}</TableCell>;
    $[97] = priceContent;
    $[98] = t43;
  } else {
    t43 = $[98];
  }
  let t44;
  if ($[99] !== deleteButton || $[100] !== showDeleteCell) {
    t44 = showDeleteCell && <TableCell className={CREATE_OFFER_FLOW_TABLE_CLASSNAMES.remove}>{deleteButton}</TableCell>;
    $[99] = deleteButton;
    $[100] = showDeleteCell;
    $[101] = t44;
  } else {
    t44 = $[101];
  }
  let t45;
  if ($[102] !== t32 || $[103] !== t35 || $[104] !== t38 || $[105] !== t41 || $[106] !== t42 || $[107] !== t43 || $[108] !== t44) {
    t45 = <TableRow>{t32}{t35}{t38}{t41}{t42}{t43}{t44}</TableRow>;
    $[102] = t32;
    $[103] = t35;
    $[104] = t38;
    $[105] = t41;
    $[106] = t42;
    $[107] = t43;
    $[108] = t44;
    $[109] = t45;
  } else {
    t45 = $[109];
  }
  let t46;
  if ($[110] !== error || $[111] !== priceInfo || $[112] !== priceWarning || $[113] !== quantityError || $[114] !== quantityErrorContent) {
    t46 = (Boolean(priceWarning) || error || quantityError) && <SpaceBetween className="w-full"><div>{quantityErrorContent}</div><div>{priceInfo}</div></SpaceBetween>;
    $[110] = error;
    $[111] = priceInfo;
    $[112] = priceWarning;
    $[113] = quantityError;
    $[114] = quantityErrorContent;
    $[115] = t46;
  } else {
    t46 = $[115];
  }
  let t47;
  if ($[116] !== t45 || $[117] !== t46) {
    t47 = <Media greaterThanOrEqual="md">{t45}{t46}</Media>;
    $[116] = t45;
    $[117] = t46;
    $[118] = t47;
  } else {
    t47 = $[118];
  }
  let t48;
  if ($[119] !== t31 || $[120] !== t47) {
    t48 = <>{t31}{t47}</>;
    $[119] = t31;
    $[120] = t47;
    $[121] = t48;
  } else {
    t48 = $[121];
  }
  return t48;
};
export const CreateOfferFlowTableRowSkeleton = () => {
  const $ = _c(13);
  const {
    size
  } = useTable();
  const imageSize = TABLE_SIZES[size].image;
  let t0;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t0 = <SkeletonBlock />;
    $[0] = t0;
  } else {
    t0 = $[0];
  }
  let t1;
  if ($[1] !== imageSize) {
    t1 = <ItemAvatar className="rounded" size={imageSize}>{t0}</ItemAvatar>;
    $[1] = imageSize;
    $[2] = t1;
  } else {
    t1 = $[2];
  }
  let t2;
  if ($[3] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = <ItemContent><Flex className="gap-2"><SkeletonLine className="h-4 w-16" /></Flex></ItemContent>;
    $[3] = t2;
  } else {
    t2 = $[3];
  }
  let t3;
  if ($[4] !== t1) {
    t3 = <TableCell className={CREATE_OFFER_FLOW_TABLE_CLASSNAMES.item}><Item variant="unstyled">{t1}{t2}</Item></TableCell>;
    $[4] = t1;
    $[5] = t3;
  } else {
    t3 = $[5];
  }
  let t4;
  if ($[6] === Symbol.for("react.memo_cache_sentinel")) {
    t4 = <TableCell className={CREATE_OFFER_FLOW_TABLE_CLASSNAMES.floor}><SkeletonLine className="h-4 w-20" /></TableCell>;
    $[6] = t4;
  } else {
    t4 = $[6];
  }
  let t5;
  if ($[7] === Symbol.for("react.memo_cache_sentinel")) {
    t5 = <TableCell className={CREATE_OFFER_FLOW_TABLE_CLASSNAMES.bestOffer}><SkeletonLine className="h-4 w-20" /></TableCell>;
    $[7] = t5;
  } else {
    t5 = $[7];
  }
  let t6;
  if ($[8] === Symbol.for("react.memo_cache_sentinel")) {
    t6 = <TableCell className={CREATE_OFFER_FLOW_TABLE_CLASSNAMES.cost}><SkeletonLine className="h-4 w-20" /></TableCell>;
    $[8] = t6;
  } else {
    t6 = $[8];
  }
  let t7;
  if ($[9] === Symbol.for("react.memo_cache_sentinel")) {
    t7 = <TableCell className={CREATE_OFFER_FLOW_TABLE_CLASSNAMES.proceeds}><SkeletonLine className="h-4 w-20" /></TableCell>;
    $[9] = t7;
  } else {
    t7 = $[9];
  }
  let t8;
  if ($[10] === Symbol.for("react.memo_cache_sentinel")) {
    t8 = <TableCell className={CREATE_OFFER_FLOW_TABLE_CLASSNAMES.price}><SkeletonLine className="h-8 w-full" /></TableCell>;
    $[10] = t8;
  } else {
    t8 = $[10];
  }
  let t9;
  if ($[11] !== t3) {
    t9 = <TableRow>{t3}{t4}{t5}{t6}{t7}{t8}</TableRow>;
    $[11] = t3;
    $[12] = t9;
  } else {
    t9 = $[12];
  }
  return t9;
};